import type { AvatarOwnProps as MuiAvatarProps } from '@mui/material/Avatar';

import Skeleton from '@mui/material/Skeleton';
import MuiAvatar from '@mui/material/Avatar';
import classNames from 'classnames';
import React from 'react';

import { toInitials } from '../../utils/initials';
import { qaProp } from '../../utils/qaProp';
import './Avatar.scss';

export interface AvatarProps extends Omit<MuiAvatarProps, 'children'> {
  size?: 'default' | 'large' | 'small' | 'tiny';
  label?: null | string;
  isLoading?: boolean;
  className?: string;
  // Overrides the label-initials fallback (e.g. a group placeholder icon)
  // when there is no `src` to show. Ignored while `src` is set.
  icon?: React.ReactNode;
  qa?: string;
}

/**
 * `sx` is DESTRUCTURED and forwarded, and that is the fix.
 *
 * The props type extends `MuiAvatarProps`, so `sx` typechecks at every call
 * site — but the component takes a fixed prop list and spreads no rest object,
 * so it was silently discarded. `AccountCard` computes an `avatarSizeSx` from
 * its `size` prop and passes it, and `ProfileLogo` declares `size?: number` and
 * threads it down: three layers of code whose only purpose is to size this
 * avatar, none of which has ever had any effect.
 *
 * Only `AccountCard` passes `sx` to THIS `Avatar` (every other `sx` on an
 * avatar in the app is on MUI's own), so honouring it changes the rendered size
 * exactly where the author asked for one — the profile-logo control, and the
 * 40px variant in the organization clients table.
 */
const Avatar = ({
  className,
  icon,
  isLoading,
  label,
  qa,
  size = 'default',
  src,
  sx,
  variant = 'circular',
}: AvatarProps) => {
  const shortenedLabel = React.useMemo(() => {
    if (src) return;
    return toInitials(label) || undefined;
  }, [label, src]);

  return (
    <MuiAvatar
      {...qaProp(qa, '__avatar')}
      className={classNames(
        'Mfw-Avatar-root',
        {
          'Mfw-Avatar-root--large ': size === 'large',
          'Mfw-Avatar-root--small ': size === 'small',
          'Mfw-Avatar-root--tiny ': size === 'tiny',
        },
        className,
      )}
      variant={variant}
      sizes={size}
      src={src}
      sx={sx}
    >
      {!isLoading && !src && icon}
      {!isLoading && !icon && shortenedLabel}
      {isLoading && (
        <Skeleton variant="circular" height="inherit" width="inherit" />
      )}
    </MuiAvatar>
  );
};

export default Avatar;
